import React, { useState, useEffect, useContext, useCallback } from 'react';
import useAnalytics from '../../analytics/useAnalytics';
import { EVT_WALLET_TOPUP } from '../../analytics/events';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Divider,
} from '@mui/material';
import {
  AddRounded,
  TrendingUpRounded,
  TrendingDownRounded,
  ReceiptLongRounded,
  Download,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import PageShell from '../../components/shared/PageShell';
import SectionHeader from '../../components/shared/SectionHeader';
import SurfaceCard from '../../components/shared/SurfaceCard';
import AnimatedNumber from '../../components/shared/AnimatedNumber';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import BottomSheet from '../../components/shared/BottomSheet';
import { RADIUS, PALETTE_LIGHT } from '../../theme/tokens';

const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const PRESET_AMOUNTS = [100, 200, 500, 1000];

// Load Razorpay script — verbatim from original
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

function TransactionRow({ txn, onDownloadInvoice }) {
  const theme = useTheme();
  const isCredit = txn.type === 'credit';

  const iconBg = isCredit
    ? `rgba(${theme.palette.mode === 'light' ? '22,163,74' : '74,222,128'},0.12)`
    : `rgba(${theme.palette.mode === 'light' ? '242,101,34' : '255,138,80'},0.12)`;

  const iconColor = isCredit ? theme.palette.success.main : theme.palette.primary.main;

  const formattedDate = new Date(txn.created_at).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.75,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: iconBg,
          color: iconColor,
          flexShrink: 0,
        }}
      >
        {isCredit ? <TrendingUpRounded /> : <TrendingDownRounded />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {txn.description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formattedDate}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box>
          <Typography
            variant="body2"
            fontWeight={700}
            fontFamily={MONO_FONT}
            color={isCredit ? 'success.main' : 'primary.main'}
          >
            {isCredit ? '+' : '−'}{txn.amount_display}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontFamily={MONO_FONT}>
            {txn.balance_after_display}
          </Typography>
        </Box>
        {isCredit && (
          <IconButton
            size="small"
            onClick={() => onDownloadInvoice(txn.id)}
            aria-label="Download invoice"
            sx={{ color: 'text.tertiary', '&:hover': { color: 'primary.main' } }}
          >
            <Download sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

function TopUpSheet({ open, onClose, user, onSuccess, onError }) {
  const [amount, setAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [scriptError, setScriptError] = useState(false);

  const handlePreset = (val) => {
    if (!processing) setAmount(String(val));
  };

  const handleClose = () => {
    if (!processing) {
      setAmount('');
      setScriptError(false);
      onClose();
    }
  };

  const handleContinue = async () => {
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount < 1) {
      onError('Minimum amount is ₹1');
      return;
    }
    if (numAmount > 10000) {
      onError('Maximum amount is ₹10,000');
      return;
    }

    setProcessing(true);
    setScriptError(false);

    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      setScriptError(true);
      setProcessing(false);
      return;
    }

    try {
      const orderRes = await api.post('/wallet/add-money', { amount: numAmount });
      const { order_id, amount: orderAmount, key_id, prefill } = orderRes.data.data;

      const options = {
        key: key_id,
        amount: orderAmount,
        currency: 'INR',
        name: 'BookMyLibrary',
        description: 'Wallet Top-up',
        order_id: order_id,
        prefill: {
          name: prefill?.name || user?.name || '',
          email: prefill?.email || user?.email || '',
          contact: prefill?.contact || user?.phone || '',
        },
        theme: {
          color: PALETTE_LIGHT.primary.main,
        },
        handler: async function (response) {
          try {
            await api.post('/wallet/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setAmount('');
            onClose();
            onSuccess(numAmount);
          } catch (err) {
            onError('Payment verification failed. Please contact support.');
          }
          setProcessing(false);
        },
        modal: {
          ondismiss: function () {
            setProcessing(false);
            onError('Payment cancelled');
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        setProcessing(false);
        onError(response.error.description || 'Payment failed. Please try again.');
      });
      razorpay.open();
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  const retryScript = () => {
    setScriptError(false);
    // remove cached broken script so loadRazorpayScript re-appends
    if (!window.Razorpay) {
      const existing = document.querySelector('script[src*="razorpay"]');
      if (existing) existing.remove();
    }
    handleContinue();
  };

  return (
    <BottomSheet open={open} onClose={handleClose} title="Add Money">
      <Box sx={{ pb: 2 }}>
        {scriptError && (
          <Alert
            severity="error"
            action={
              <Box
                component="span"
                onClick={retryScript}
                sx={{ cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', fontSize: '0.8rem' }}
              >
                Retry
              </Box>
            }
            sx={{ mb: 2, borderRadius: RADIUS.md }}
          >
            Payment gateway failed to load.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" mb={2}>
          Add money to your wallet for quick bookings and subscriptions.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
          {PRESET_AMOUNTS.map((preset) => (
            <Chip
              key={preset}
              label={`₹${preset}`}
              onClick={() => handlePreset(preset)}
              disabled={processing}
              variant={amount === String(preset) ? 'filled' : 'outlined'}
              color="primary"
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>

        <TextField
          fullWidth
          label="Amount (₹)"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          disabled={processing}
          inputProps={{ min: 1, max: 10000 }}
          helperText="Min ₹1, Max ₹10,000"
          sx={{ mb: 2.5 }}
        />

        <Box
          component="button"
          onClick={handleContinue}
          disabled={processing || !amount}
          sx={{
            width: '100%',
            py: 1.75,
            border: 'none',
            borderRadius: RADIUS.pill,
            bgcolor: processing || !amount ? 'action.disabledBackground' : 'primary.main',
            color: processing || !amount ? 'text.disabled' : 'primary.contrastText',
            fontWeight: 700,
            fontSize: '1rem',
            cursor: processing || !amount ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            transition: 'background-color 0.18s',
          }}
        >
          {processing ? (
            <>
              <CircularProgress size={18} color="inherit" />
              Processing...
            </>
          ) : (
            `Pay ₹${amount || '0'}`
          )}
        </Box>
      </Box>
    </BottomSheet>
  );
}

function StudentWallet() {
  const { isAuthenticated, user } = useContext(AuthContext);
  const { track } = useAnalytics();
  const theme = useTheme();

  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpSuccess, setTopUpSuccess] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const fetchWalletData = useCallback(async () => {
    try {
      setLoading(true);
      const [balanceRes, txRes] = await Promise.all([
        api.get('/wallet/balance'),
        api.get('/wallet/transactions'),
      ]);
      setWallet(balanceRes.data.data.wallet);
      setTransactions(txRes.data.data.transactions || []);
    } catch (err) {
      showToast('Failed to load wallet', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchWalletData();
  }, [isAuthenticated, fetchWalletData]);

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
    setTimeout(() => setToast((t) => ({ ...t, open: false })), 4000);
  };

  const handleTopUpSuccess = (numAmount) => {
    const amountPaise = numAmount * 100;
    track(EVT_WALLET_TOPUP, { amountPaise }, numAmount);
    setTopUpSuccess(true);
    setTimeout(() => setTopUpSuccess(false), 2000);
    showToast(`₹${numAmount} added to wallet!`, 'success');
    fetchWalletData();
  };

  const handleDownloadInvoice = async (transactionId) => {
    try {
      const response = await api.get(`/wallet/transactions/${transactionId}/invoice`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${transactionId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast('Invoice downloaded', 'success');
    } catch (err) {
      showToast('Failed to download invoice', 'error');
    }
  };

  const balancePaise = wallet?.balance ?? 0;
  const balanceRupees = balancePaise / 100;

  if (loading) {
    return (
      <PageShell title="Wallet" backTo={-1}>
        <LoadingSkeleton variant="hero" sx={{ height: 200, mb: 2 }} />
        <LoadingSkeleton variant="listItem" count={4} />
      </PageShell>
    );
  }

  return (
    <PageShell title="Wallet" backTo={-1}>
      {/* Balance hero */}
      <SurfaceCard
        sx={{ mb: 2.5, borderLeft: (t) => `4px solid ${t.palette.primary.main}`, position: 'relative' }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          display="block"
          mb={0.25}
        >
          Wallet balance
        </Typography>

        <AnimatePresence>
          {topUpSuccess && (
            <motion.div
              key="pulse"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              transition={{ duration: 0.3 }}
              style={{ position: 'absolute', inset: 0, pointerEvents: 'none', borderRadius: RADIUS.md, border: '2px solid', borderColor: 'primary.main', opacity: 0.4 }}
            />
          )}
        </AnimatePresence>

        <Typography
          variant="display"
          fontWeight={800}
          color="text.primary"
          aria-label={`Wallet balance: ${balanceRupees.toLocaleString('en-IN')} rupees`}
        >
          &#x20b9;
          <AnimatedNumber
            value={balanceRupees}
            format={(n) => Math.round(n).toLocaleString('en-IN')}
            duration={0.9}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          />
        </Typography>

        <Box
          component="button"
          onClick={() => setTopUpOpen(true)}
          aria-label="Add money to wallet"
          sx={{
            mt: 2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2,
            py: 1,
            bgcolor: 'primary.main',
            border: 'none',
            borderRadius: RADIUS.pill,
            color: 'primary.contrastText',
            fontWeight: 700,
            fontSize: '0.875rem',
            cursor: 'pointer',
            letterSpacing: '0.01em',
            transition: 'opacity 0.18s',
            '&:hover': { opacity: 0.88 },
          }}
        >
          <AddRounded sx={{ fontSize: 18 }} />
          Add Money
        </Box>
      </SurfaceCard>

      {/* Transactions */}
      <SectionHeader title="Recent transactions" icon={<ReceiptLongRounded sx={{ fontSize: 20 }} />} />

      {transactions.length === 0 ? (
        <EmptyState
          icon={<ReceiptLongRounded />}
          title="No transactions yet"
          description="Add money to your wallet to get started."
          actionLabel="Add Money"
          onAction={() => setTopUpOpen(true)}
        />
      ) : (
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: RADIUS.lg,
            boxShadow: theme.shadowsExt?.sm,
            overflow: 'hidden',
          }}
        >
          {transactions.map((txn, idx) => (
            <React.Fragment key={txn.id}>
              {idx > 0 && <Divider sx={{ mx: 2 }} />}
              <TransactionRow txn={txn} onDownloadInvoice={handleDownloadInvoice} />
            </React.Fragment>
          ))}
        </Box>
      )}

      <TopUpSheet
        open={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        user={user}
        onSuccess={handleTopUpSuccess}
        onError={(msg) => showToast(msg, 'error')}
      />

      {/* Toast */}
      <AnimatePresence>
        {toast.open && (
          <motion.div
            key="toast"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            style={{
              position: 'fixed',
              bottom: 90,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2000,
              width: '90%',
              maxWidth: 360,
            }}
          >
            <Alert
              severity={toast.severity}
              onClose={() => setToast((t) => ({ ...t, open: false }))}
              sx={{ borderRadius: RADIUS.lg, boxShadow: theme.shadowsExt?.md }}
            >
              {toast.message}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>
    </PageShell>
  );
}

export default StudentWallet;
